import { Button, Divider, Box } from '@mui/material';
import { resetFiltersButtonSx } from 'components/Search/styles';
import { Fragment, memo } from 'react';
import { useSearch } from '../../SearchContextProvider';
import { useAdvancedSearchContext } from './useAdvancedSearchContext';
import { useTranslation } from 'react-i18next';

/**
 * Advanced Search Filters Row
 * - active/applied filters below the search bar
 * - reset filters button
 *
 * advancedFilterFieldsComponent is component to render
 * the advanced filter fields eg: <EditorialFilterFields />
 */

type SearchFiltersRowProps = {
  advancedFilterFieldsComponent?: React.ReactNode;
};

export const AdvancedSearchFiltersRow = memo(
  ({ advancedFilterFieldsComponent }: SearchFiltersRowProps) => {
    const { t } = useTranslation();
    const { resetQpFilters } = useSearch();
    const { advancedFiltersCount } = useAdvancedSearchContext();
    const showAdvancedFilters = Boolean(advancedFiltersCount);

    const handleResetFilters = () => {
      resetQpFilters();
    };

    const uiResetFilters = t('blox.actions.reset_filters');

    // no filters component provided or no applied filters
    const showFields = Boolean(advancedFilterFieldsComponent && showAdvancedFilters);

    return (
      <Box
        id="search-advanced-filters-row-container"
        data-testid="search-advanced-filters-row-container"
        sx={{
          px: 1,
          pb: 0.5,
          pt: 0,
          gap: 0.5,
          display: 'flex',
          flexWrap: {
            xs: 'nowrap',
            sm: 'wrap',
          },
        }}
      >
        {showFields && (
          <Fragment>
            {/** Active Filters Row Component */}
            {advancedFilterFieldsComponent}

            {/** Reset Filters Button */}
            <Divider
              orientation="vertical"
              flexItem
            />

            <Button
              variant="text"
              onClick={handleResetFilters}
              size="small"
              color="info"
              sx={resetFiltersButtonSx}
            >
              {uiResetFilters}
            </Button>
          </Fragment>
        )}
      </Box>
    );
  },
);

AdvancedSearchFiltersRow.displayName = 'AdvancedSearchFiltersRow';
